import { useState, useEffect } from 'react';
import { X, Search, ShoppingCart, Trash2, CheckCircle } from 'lucide-react';
import { stockApi, ventasApi } from '../lib/api';
import { topeVenta } from '../lib/autorizacionKit';
import { coincideProducto } from '../lib/buscar';
import { toast } from 'sonner';

interface ItemCarrito {
  producto: any;
  cantidad: number;
}

interface CarritoMantenimientoModalProps {
  placaBus: string;
  onClose: () => void;
  onCompletada?: () => void;
}

// Carrito para agregar varias piezas de una vez a las "piezas pendientes" de un
// bus. NO imprime nada aqui: las ventas quedan abiertas (cerrada=false) hasta
// que se cierren e impriman desde el boton "Cerrar e Imprimir" en Piezas Usadas.
const CarritoMantenimientoModal = ({ placaBus, onClose, onCompletada }: CarritoMantenimientoModalProps) => {
  const [productos, setProductos] = useState<any[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [carrito, setCarrito] = useState<ItemCarrito[]>([]);
  const [procesando, setProcesando] = useState(false);
  const [terminado, setTerminado] = useState(false);
  // Antes esto quedaba fijo en 'efectivo' sin preguntar -- mismo bug que en
  // VentasCompleto.tsx (asignarCarritoABus). Default a crédito porque una pieza
  // agregada al pendiente de un bus casi siempre es a cuenta de ese bus.
  const [tipoPago, setTipoPago] = useState<'credito' | 'efectivo'>('credito');

  useEffect(() => {
    stockApi.getFull().then((data) => setProductos((data || []).filter((p: any) => p.estado === 'activo'))).catch(() => {});
  }, []);

  const productosFiltrados = busqueda.trim()
    ? productos.filter((p) => coincideProducto(busqueda, p))
    : productos;

  const agregarAlCarrito = (producto: any) => {
    setCarrito((prev) => {
      const existente = prev.find((i) => i.producto.id === producto.id);
      if (existente) {
        return prev.map((i) =>
          i.producto.id === producto.id
            ? { ...i, cantidad: Math.min(i.cantidad + 1, topeVenta(producto)) }
            : i
        );
      }
      return [...prev, { producto, cantidad: 1 }];
    });
  };

  const actualizarCantidad = (productoId: string, cantidad: number) => {
    if (cantidad <= 0) {
      setCarrito((prev) => prev.filter((i) => i.producto.id !== productoId));
      return;
    }
    setCarrito((prev) =>
      prev.map((i) => (i.producto.id === productoId ? { ...i, cantidad: Math.min(cantidad, topeVenta(i.producto)) } : i))
    );
  };

  // El precio del stock ya es el precio final (no se agrega IGV en ningún otro lugar del
  // sistema, ver el mismo comentario en VentasCompleto.tsx) -- antes este total se mostraba
  // con un 1.18 extra que nunca se reflejaba en lo que realmente se registraba.
  const totalCarrito = carrito.reduce(
    (sum, i) => sum + (i.producto.precio_venta || i.producto.precio || 0) * i.cantidad,
    0
  );

  const agregarAPendientes = async () => {
    if (carrito.length === 0) return;
    setProcesando(true);
    try {
      for (const item of carrito) {
        const precio = item.producto.precio_venta || item.producto.precio || 0;
        const subtotal = precio * item.cantidad;
        const igv = 0;
        await ventasApi.crearConCarrito({
          cliente_nombre: 'Cliente Contado',
          placa_bus: placaBus,
          tipo_pago: tipoPago,
          items: [{ id: item.producto.id, nombre: item.producto.nombre, cantidad: item.cantidad, precio }],
          subtotal,
          igv,
          total: subtotal,
          cerrada: false,
          observaciones: 'Pieza pendiente para mantenimiento (carrito)'
        });
      }
      const etiquetaTipo = tipoPago === 'credito' ? 'a crédito' : 'al contado';
      toast.success(`${carrito.length} pieza(s) agregada(s) al pendiente del bus ${placaBus} (${etiquetaTipo})`);
      setTerminado(true);
      onCompletada?.();
    } catch (error: any) {
      console.error('Error agregando piezas pendientes:', error);
      toast.error('Error al agregar piezas: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
    >
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ShoppingCart className="text-teal-600" size={20} />
            Agregar Piezas — Bus {placaBus}
          </h2>
          {!procesando && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          )}
        </div>

        <div className="p-6 space-y-4">
          {terminado ? (
            <div className="text-center py-8">
              <CheckCircle className="mx-auto text-green-500 mb-3" size={48} />
              <p className="font-semibold text-lg">Piezas agregadas al pendiente del bus</p>
              <p className="text-gray-500 text-sm mt-1">
                Se imprimen cuando cierres desde "Piezas Usadas".
              </p>
              <button onClick={onClose} className="mt-6 bg-gray-700 hover:bg-gray-800 text-white px-6 py-2 rounded-lg">
                Cerrar
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar producto por nombre o código..."
                  className="w-full pl-9 pr-3 py-2 border rounded-lg"
                  autoFocus
                />
              </div>
              <div className="max-h-56 overflow-y-auto space-y-2">
                {productosFiltrados.slice(0, 20).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => agregarAlCarrito(p)}
                    disabled={topeVenta(p) <= 0}
                    className="w-full text-left border rounded-lg p-3 hover:bg-gray-50 disabled:opacity-40 flex justify-between items-center"
                  >
                    <div>
                      <p className="font-medium">{p.nombre}{p.es_kit && <span className="ml-2 px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-xs font-semibold">KIT</span>}</p>
                      <p className="text-xs text-gray-500">{p.es_kit ? 'Kits armables' : 'Stock'}: {p.stock}</p>
                    </div>
                    <p className="font-bold text-green-600">S/ {(p.precio_venta || p.precio || 0).toFixed(2)}</p>
                  </button>
                ))}
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">Piezas a agregar ({carrito.length})</h3>
                {carrito.length === 0 ? (
                  <p className="text-gray-500 text-sm">Agrega piezas de la lista de arriba</p>
                ) : (
                  <div className="space-y-2">
                    {carrito.map((item) => (
                      <div key={item.producto.id} className="flex items-center justify-between border rounded-lg p-2">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{item.producto.nombre}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => actualizarCantidad(item.producto.id, item.cantidad - 1)} className="bg-gray-200 w-6 h-6 rounded">-</button>
                          <span className="w-6 text-center text-sm">{item.cantidad}</span>
                          <button onClick={() => actualizarCantidad(item.producto.id, item.cantidad + 1)} className="bg-gray-200 w-6 h-6 rounded">+</button>
                          <button onClick={() => actualizarCantidad(item.producto.id, 0)} className="text-red-500 ml-2">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between font-bold pt-2 border-t">
                      <span>Total:</span>
                      <span>S/ {totalCarrito.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Tipo de pago</label>
                <select
                  value={tipoPago}
                  onChange={(e) => setTipoPago(e.target.value as 'credito' | 'efectivo')}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="credito">A crédito del bus</option>
                  <option value="efectivo">Al contado (se cobra ahora)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {tipoPago === 'credito'
                    ? 'La deuda del bus sube de inmediato al agregar, aunque la pieza siga pendiente de cerrar.'
                    : 'No se suma a la deuda del bus. No entra a Caja Central hasta que se cierre esta pieza.'}
                </p>
              </div>

              <p className="text-xs text-gray-500">
                Esto NO imprime nada todavía. Las piezas quedan pendientes en "Piezas Usadas" hasta que las cierres.
              </p>

              <button
                onClick={agregarAPendientes}
                disabled={carrito.length === 0 || procesando}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
              >
                {procesando ? 'Agregando...' : 'Agregar al Pendiente del Bus'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarritoMantenimientoModal;
